/**
 * Seção "Propostas" no detalhe do lead (PR-M1-01c-iii.a+.b).
 *
 * Lista rascunhos + propostas enviadas/aceitas/recusadas do lead em
 * cards organizados. Cada card mostra: tipo, status, valor formatado,
 * data de criação, preview das notas. Ações rápidas: Excluir (apenas
 * rascunho); Enviar (disabled por ora — vem no PR-M1-01c-ii.a).
 *
 * **Por que arquivo separado**: mesma estratégia de `LeadFormSubmissionsSection`
 * — mantém o `LeadDetailPage` focado em orquestração.
 */
import { useState } from 'react';
import { Button, Skeleton, toast } from '@kmgestao/ui';

import { isTerminalStage, type LeadStage } from '../../../features/leads/index.js';
import {
  getProposalStatusStyle,
  PLAN_LABELS,
  PROPOSAL_STATUS_LABELS,
  PROPOSAL_TYPE_LABELS,
  useDeleteProposal,
  useLeadProposals,
  useSendProposal,
  type LeadProposalSummary,
} from '../../../features/proposals/index.js';
import { copyToClipboard } from '../../../lib/clipboard.js';

import { NewProposalDialog } from './NewProposalDialog.js';
import { ProposalContractActions } from './ProposalContractActions.js';
import { ProposalPreviewModal } from './ProposalPreviewModal.js';
import { ViewProposalModal } from './ViewProposalModal.js';

export interface LeadProposalsSectionProps {
  leadId: string;
  /** Usado no título do dialog de edição e na prévia ("Proposta para X"). */
  leadName: string;
  /** Nome fantasia da clínica do lead — usado no cabeçalho da prévia
   * como destinatário principal. */
  leadClinicNameGuess: string | null;
  /** Stage atual do lead. Determina ações disponíveis (gerar contrato vs.
   * resumo) e modo do card (expandido vs. collapsed em estado terminal).
   * PR-M1-01c-iii.b. */
  leadStage: LeadStage;
}

/** Formata valor da proposta em linha legível por tipo. */
function formatInvestment(p: LeadProposalSummary): string {
  const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  const parts: string[] = [];
  if (
    (p.proposal_type === 'saas' || p.proposal_type === 'saas_consulting') &&
    p.plan_key !== null
  ) {
    // Prefer nome do banco (suporta planos custom cadastrados em
    // /superadmin/billing/plans). PLAN_LABELS fica só como fallback
    // pros 3 canônicos (legacy).
    const planLabel = p.plan_name_pt_br ?? PLAN_LABELS[p.plan_key] ?? p.plan_key;
    if (p.custom_price_brl !== null) {
      parts.push(`${planLabel} · ${brl.format(p.custom_price_brl)}/mês (preço custom)`);
    } else if (p.discount_percent !== null) {
      parts.push(`${planLabel} · ${p.discount_percent}% de desconto`);
    } else {
      parts.push(`${planLabel}`);
    }
  }
  if (
    (p.proposal_type === 'consulting' || p.proposal_type === 'saas_consulting') &&
    p.consulting_installments_count !== null &&
    p.consulting_installment_brl !== null
  ) {
    parts.push(
      `${p.consulting_installments_count}× ${brl.format(p.consulting_installment_brl)} ` +
        `(${p.consulting_duration_months ?? '?'} meses)`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : 'Sem valores configurados';
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function ProposalCard({
  proposal,
  leadId,
  leadName,
  leadClinicNameGuess,
  leadStage,
}: {
  proposal: LeadProposalSummary;
  leadId: string;
  leadName: string;
  leadClinicNameGuess: string | null;
  leadStage: LeadStage;
}) {
  const deleteProposal = useDeleteProposal();
  const sendProposal = useSendProposal();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Em estado terminal (signed/provisioned), o card vem collapsed por
  // default — proposta virou contrato assinado, dado é "consultável" e
  // não "trabalhável". Karla pode expandir clicando no header.
  // PR-M1-01c-iii.b (2026-04-25).
  const startCollapsed = isTerminalStage(leadStage);
  const [collapsed, setCollapsed] = useState(startCollapsed);

  const isDraft = proposal.status === 'draft';
  const isInNegotiation = proposal.status === 'in_negotiation';
  const isSent = proposal.status !== 'draft';
  // Proposta é editável em qualquer estado "pré-fechamento":
  //   - `draft` (nunca enviada)
  //   - `sent` (enviada, lead não abriu ainda)
  //   - `viewed` (lead abriu mas não agiu)
  //   - `in_negotiation` (lead mandou dúvida)
  //
  // PR-M1-01c-ii.i (2026-04-24): Alexandre reportou que após reenvio o
  // botão Editar sumia. Cenário real: Karla conversa com lead por fora
  // (WhatsApp/ligação), ele pede ajuste, ela quer editar + reenviar de
  // novo. Ampliamos pra permitir isso.
  //
  // `accepted/rejected/expired` continuam travados — proposta fechou.
  const isEditable =
    isDraft || proposal.status === 'sent' || proposal.status === 'viewed' || isInNegotiation;
  // Reenvio segue a mesma regra de editabilidade (send-proposal handler
  // aceita todos esses estados e preserva o public_token).
  const canSend = isEditable;
  const investment = formatInvestment(proposal);
  const notesPreview = proposal.notes ? proposal.notes.slice(0, 180) : null;

  const handleDelete = () => {
    deleteProposal.mutate(
      { id: proposal.id, lead_id: leadId },
      {
        onSuccess: () => toast.success('Rascunho excluído.'),
        onError: (err) => toast.error('Falha ao excluir', { description: (err as Error).message }),
      },
    );
  };

  const handleSend = () => {
    sendProposal.mutate(
      { proposal_id: proposal.id, lead_id: leadId },
      {
        onSuccess: async (res) => {
          setConfirmSend(false);
          if (res.email_status === 'sent') {
            toast.success('Proposta enviada por email.', {
              description: 'Link público também está disponível para copiar.',
            });
          } else if (res.email_status === 'skipped') {
            toast.info('Proposta marcada como enviada (email pulado em modo dev).');
          } else {
            toast.warning('Proposta enviada, mas o email falhou.', {
              description: 'Copie o link e envie pelo canal preferido.',
            });
          }
          // Copia link público automaticamente — Karla quase sempre quer
          // enviar pelo WhatsApp também, então economizar 1 clique vale.
          const copied = await copyToClipboard(res.public_url);
          if (copied) {
            toast.info('Link público copiado para a área de transferência.');
          }
        },
        onError: (err) =>
          toast.error('Falha ao enviar proposta', { description: (err as Error).message }),
      },
    );
  };

  const handleCopyLink = async () => {
    if (!proposal.public_token) return;
    const base = window.location.origin;
    const url = `${base}/proposta/${proposal.public_token}`;
    const ok = await copyToClipboard(url);
    if (ok) toast.success('Link copiado.');
    else toast.error('Não foi possível copiar o link.');
  };

  const statusStyle = getProposalStatusStyle(proposal.status);
  const isMilestone = statusStyle.level === 'milestone';

  return (
    <article
      className={[
        'space-y-3 rounded-lg border border-border bg-card p-4 shadow-sm transition-colors',
        // Eventos-marco (proposta aceita) ganham reforço visual lateral —
        // borda accent + bg sutil — pra Karla bater o olho na lista de
        // propostas e identificar onde está cada uma sem ler texto.
        isMilestone ? 'border-l-4 border-l-accent bg-accent/[0.04]' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Header é clicável quando o card pode ser collapsed (estado
          terminal). Em estados ativos não tem cursor pointer — não
          queremos confundir Karla achando que toda hora pode dobrar. */}
      <header
        className={[
          'flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between',
          startCollapsed ? 'cursor-pointer select-none' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={startCollapsed ? () => setCollapsed((v) => !v) : undefined}
        role={startCollapsed ? 'button' : undefined}
        aria-expanded={startCollapsed ? !collapsed : undefined}
        tabIndex={startCollapsed ? 0 : undefined}
        onKeyDown={
          startCollapsed
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCollapsed((v) => !v);
                }
              }
            : undefined
        }
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground sm:text-base">
            {PROPOSAL_TYPE_LABELS[proposal.proposal_type]}
            {startCollapsed ? (
              <span aria-hidden="true" className="ml-2 text-xs text-muted-foreground">
                {collapsed ? '▸' : '▾'}
              </span>
            ) : null}
          </p>
          <p className="text-xs text-muted-foreground">
            Criada em {formatDateTime(proposal.created_at)}
          </p>
        </div>
        <span className={statusStyle.badgeClassName}>
          {statusStyle.icon ? (
            <span aria-hidden="true" className="leading-none">
              {statusStyle.icon}
            </span>
          ) : null}
          <span>{PROPOSAL_STATUS_LABELS[proposal.status]}</span>
        </span>
      </header>

      {collapsed ? null : (
        <>
          <div className="space-y-1 text-sm">
            <p className="font-medium text-foreground">{investment}</p>
            {proposal.trial_days !== null && proposal.trial_days > 0 ? (
              <p className="text-xs text-muted-foreground">
                Trial de {proposal.trial_days} dia{proposal.trial_days === 1 ? '' : 's'}
              </p>
            ) : null}
          </div>

          {notesPreview ? (
            <p className="line-clamp-3 whitespace-pre-wrap text-sm text-muted-foreground">
              {notesPreview}
              {proposal.notes && proposal.notes.length > 180 ? '…' : ''}
            </p>
          ) : null}

          {/* Ações de contrato — aparece quando proposta aceita. Em estado
              terminal (signed/provisioned) o componente troca pra resumo
              "Contrato assinado" sem CTA. */}
          <ProposalContractActions proposal={proposal} leadId={leadId} leadStage={leadStage} />

          {/* Badge de dúvida pendente — aparece quando lead enviou
              questioning_message e proposta está em in_negotiation.
              Resposta acontece out-of-band (email). Ver PR-M1-01c-ii.c. */}
          {proposal.questioning_message && proposal.status === 'in_negotiation' ? (
            <div
              className="space-y-1 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm"
              role="status"
            >
              <p className="font-semibold text-foreground">⚠️ Dúvida recebida do lead</p>
              <p className="line-clamp-2 whitespace-pre-wrap text-muted-foreground">
                {proposal.questioning_message}
              </p>
              <p className="text-xs text-muted-foreground">
                Clique em <strong>Detalhes</strong> para ver a mensagem completa. Responda pelo
                email de contato do lead — o registro in-app é só para consulta.
              </p>
            </div>
          ) : null}

          <footer className="flex flex-wrap items-center gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={() => setViewOpen(true)}>
              Detalhes
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)}>
              Ver prévia pública
            </Button>
            {isEditable ? (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                {isDraft ? 'Editar' : 'Editar proposta'}
              </Button>
            ) : null}
            {isSent && proposal.public_token ? (
              <Button size="sm" variant="outline" onClick={handleCopyLink}>
                Copiar link público
              </Button>
            ) : null}
            {canSend ? (
              confirmSend ? (
                <>
                  <Button size="sm" onClick={handleSend} disabled={sendProposal.isPending}>
                    {sendProposal.isPending
                      ? isDraft
                        ? 'Enviando…'
                        : 'Reenviando…'
                      : isDraft
                        ? 'Confirmar envio'
                        : 'Confirmar reenvio'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmSend(false)}
                    disabled={sendProposal.isPending}
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button size="sm" onClick={() => setConfirmSend(true)}>
                  {isDraft ? 'Enviar proposta' : 'Reenviar com correções'}
                </Button>
              )
            ) : null}
            {isDraft ? (
              confirmDelete ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive hover:text-destructive"
                    onClick={handleDelete}
                    disabled={deleteProposal.isPending}
                  >
                    {deleteProposal.isPending ? 'Excluindo…' : 'Confirmar exclusão'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmDelete(false)}
                    disabled={deleteProposal.isPending}
                  >
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setConfirmDelete(true)}
                >
                  Excluir rascunho
                </Button>
              )
            ) : null}
          </footer>

          <ViewProposalModal
            open={viewOpen}
            onOpenChange={setViewOpen}
            proposal={proposal}
            onEdit={isEditable ? () => setEditOpen(true) : undefined}
          />

          <ProposalPreviewModal
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            proposal={proposal}
            lead={{ id: leadId, full_name: leadName, clinic_name_guess: leadClinicNameGuess }}
            showSendAction={canSend}
            onEdit={isEditable ? () => setEditOpen(true) : undefined}
          />

          {isEditable ? (
            <NewProposalDialog
              leadId={leadId}
              leadName={leadName}
              existingProposal={proposal}
              open={editOpen}
              onOpenChange={setEditOpen}
            />
          ) : null}
        </>
      )}
    </article>
  );
}

export function LeadProposalsSection({
  leadId,
  leadName,
  leadClinicNameGuess,
  leadStage,
}: LeadProposalsSectionProps) {
  const proposals = useLeadProposals(leadId);

  if (proposals.isLoading) {
    return (
      <div className="space-y-2" aria-busy="true" aria-live="polite">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (proposals.isError) {
    return (
      <div
        className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
        role="alert"
      >
        Não foi possível carregar as propostas agora.{' '}
        {proposals.error instanceof Error ? proposals.error.message : null}
      </div>
    );
  }

  const rows = proposals.data ?? [];

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-muted/20 p-6 text-center">
        <p className="text-sm font-medium text-foreground">
          Nenhuma proposta criada para este lead.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Use o botão &quot;Criar proposta&quot; abaixo para gerar um rascunho (com IA ou
          manualmente).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((proposal) => (
        <ProposalCard
          key={proposal.id}
          proposal={proposal}
          leadId={leadId}
          leadName={leadName}
          leadClinicNameGuess={leadClinicNameGuess}
          leadStage={leadStage}
        />
      ))}
    </div>
  );
}
